import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { exchangeRatesAPI } from '../lib/api';
import { convertAmount, getPropertyCurrency } from '../lib/exchangeRates';
import { isAnonymousAuthFlowRoute } from '../lib/apiAuth';

const ExchangeRatesContext = createContext(null);

export function ExchangeRatesProvider({ children }) {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applySnapshot = useCallback((snapshot) => {
    if (snapshot?.to_usd) {
      setRates(snapshot);
      setError(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await exchangeRatesAPI.getLatest();
      applySnapshot(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load exchange rates');
    } finally {
      setLoading(false);
    }
  }, [applySnapshot]);

  useEffect(() => {
    if (isAnonymousAuthFlowRoute()) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const convert = useCallback(
    (amount, fromCurrency, toCurrency) => {
      const from = (fromCurrency || 'USD').toUpperCase();
      const to = (toCurrency || 'USD').toUpperCase();
      const n = Number(amount);
      if (!Number.isFinite(n)) return 0;
      if (from === to) return n;
      if (!rates?.to_usd) return n;
      return convertAmount(amount, from, to, rates);
    },
    [rates],
  );

  const value = useMemo(
    () => ({
      rates,
      loading,
      error,
      refresh,
      applySnapshot,
      convert,
      getPropertyCurrency,
      trmCopPerUsd: rates?.trm_cop_per_usd ?? null,
      ratesAsOf: rates?.as_of ?? null,
      ratesStale: Boolean(rates?.stale),
    }),
    [rates, loading, error, refresh, applySnapshot, convert],
  );

  return <ExchangeRatesContext.Provider value={value}>{children}</ExchangeRatesContext.Provider>;
}

export function useExchangeRates() {
  const ctx = useContext(ExchangeRatesContext);
  if (!ctx) {
    throw new Error('useExchangeRates must be used within ExchangeRatesProvider');
  }
  return ctx;
}

export default ExchangeRatesContext;
